<template>
  <div class="gamification-dashboard">
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">Carregando gamificação...</p>
    </div>

    <div v-else-if="!dashData || !dashData.campaign" class="no-campaign">
      <div class="no-campaign-inner">
        <feather-icon icon="AwardIcon" size="56" class="text-muted mb-1" />
        <h4>Nenhuma campanha ativa</h4>
        <p class="text-muted">Quando uma campanha de gamificação estiver ativa, você verá seus pontos, ranking e conquistas aqui.</p>
      </div>
    </div>

    <div v-else>
      <div class="campaign-header">
        <div class="campaign-header-left">
          <div class="campaign-icon">
            <feather-icon icon="AwardIcon" size="20" />
          </div>
          <div>
            <h3 class="campaign-name">{{ dashData.campaign.nome }}</h3>
            <span class="campaign-period">
              {{ formatDate(dashData.campaign.data_inicio) }} - {{ formatDate(dashData.campaign.data_fim) }}
            </span>
          </div>
        </div>
        <div class="campaign-header-right">
          <b-button variant="outline-primary" size="sm" @click="showGuide = true" class="guide-btn">
            <feather-icon icon="BookOpenIcon" size="14" class="mr-25" />
            Como Ganhar
          </b-button>
          <b-badge variant="success" pill class="days-badge">{{ dashData.campaign.daysRemaining }} dias restantes</b-badge>
        </div>
      </div>

      <div class="motivational-banner" :class="motivationalClass">
        <feather-icon :icon="motivationalIcon" size="16" class="mr-50" />
        <span>{{ motivationalMessage }}</span>
      </div>

      <div v-if="dashData.campaign.premio_1_valor || dashData.campaign.premio_2_valor" class="prizes-section">
        <div class="prizes-title">
          <feather-icon icon="GiftIcon" size="16" class="mr-50" />
          Premiação desta Campanha
        </div>
        <div class="prizes-row">
          <div v-if="dashData.campaign.premio_1_valor" class="prize-card prize-gold">
            <div class="prize-place">
              <span class="prize-medal">🥇</span> 1º Lugar
            </div>
            <div class="prize-value">R$ {{ formatNumber(dashData.campaign.premio_1_valor) }}</div>
            <div v-if="dashData.campaign.premio_1_descricao" class="prize-desc">{{ dashData.campaign.premio_1_descricao }}</div>
          </div>
          <div v-if="dashData.campaign.premio_2_valor" class="prize-card prize-silver">
            <div class="prize-place">
              <span class="prize-medal">🥈</span> 2º Lugar
            </div>
            <div class="prize-value">R$ {{ formatNumber(dashData.campaign.premio_2_valor) }}</div>
            <div v-if="dashData.campaign.premio_2_descricao" class="prize-desc">{{ dashData.campaign.premio_2_descricao }}</div>
          </div>
        </div>
      </div>

      <div class="alerts-row">
        <div v-if="dashData.pointsToNext > 0" class="alert-card alert-rank">
          <feather-icon icon="TrendingUpIcon" size="15" class="mr-50" />
          <span>🔥 Faltam apenas <strong>{{ dashData.pointsToNext }} pontos</strong> para subir no ranking!</span>
        </div>
        <div v-if="dashData.pendingMissions > 0" class="alert-card alert-missions">
          <feather-icon icon="TargetIcon" size="15" class="mr-50" />
          <span>🎯 Complete missões para ganhar pontos bônus!</span>
        </div>
        <div v-if="dashData.campaign.daysRemaining <= 7 && dashData.campaign.daysRemaining > 0" class="alert-card alert-deadline">
          <feather-icon icon="ClockIcon" size="15" class="mr-50" />
          <span>⏰ A campanha termina em <strong>{{ dashData.campaign.daysRemaining }} dias</strong>! Acelere!</span>
        </div>
      </div>

      <div class="stats-grid">
        <div class="stat-card stat-points">
          <div class="stat-main">
            <span class="stat-value-lg">{{ dashData.personalStats.total_points }}</span>
            <span class="stat-label-sm">Seus Pontos</span>
          </div>
          <div class="stat-icon-box">
            <feather-icon icon="StarIcon" size="20" />
          </div>
        </div>
        <div class="stat-card stat-rank">
          <div class="stat-main">
            <span class="stat-value-lg">{{ dashData.personalStats.rank }}º</span>
            <span class="stat-label-sm">Posição</span>
          </div>
          <div class="stat-icon-box">
            <feather-icon icon="AwardIcon" size="20" />
          </div>
        </div>
        <div class="stat-card stat-week">
          <div class="stat-main">
            <span class="stat-value-lg stat-positive">+{{ dashData.personalStats.week_points }}</span>
            <span class="stat-label-sm">Esta Semana</span>
          </div>
          <div class="stat-icon-box">
            <feather-icon icon="TrendingUpIcon" size="20" />
          </div>
        </div>
        <div class="stat-card stat-badges">
          <div class="stat-main">
            <span class="stat-value-lg">{{ dashData.personalStats.total_badges }}</span>
            <span class="stat-label-sm">Medalhas</span>
          </div>
          <div class="stat-icon-box">
            <feather-icon icon="ShieldIcon" size="20" />
          </div>
        </div>
      </div>

      <div class="ranking-section">
        <div class="ranking-header">
          <div class="ranking-title">
            <feather-icon icon="BarChart2Icon" size="18" class="mr-50" />
            <span>Ranking Consolidado - Empresa</span>
          </div>
          <div class="ranking-meta">
            <span class="meta-item">
              <feather-icon icon="UsersIcon" size="13" class="mr-25" />
              {{ dashData.totalParticipants }} participantes
            </span>
            <span class="meta-sep">|</span>
            <span class="meta-item meta-highlight">{{ dashData.totalCompanyPoints }} pts totais</span>
          </div>
        </div>

        <div v-if="podium.length >= 3" class="podium">
          <div class="podium-item podium-2">
            <div class="podium-avatar">
              <div class="avatar-circle">{{ getInitials(podium[1].representantenome) }}</div>
            </div>
            <div class="podium-name">{{ getFirstName(podium[1].representantenome) }}</div>
            <div class="podium-pts">{{ podium[1].total_points }} pts</div>
            <div class="podium-base podium-base-2">2</div>
          </div>
          <div class="podium-item podium-1">
            <div class="podium-crown">👑</div>
            <div class="podium-avatar podium-avatar-1">
              <div class="avatar-circle avatar-gold">{{ getInitials(podium[0].representantenome) }}</div>
            </div>
            <div class="podium-name podium-name-1">{{ getFirstName(podium[0].representantenome) }}</div>
            <div class="podium-pts podium-pts-1">{{ podium[0].total_points }} pts</div>
            <div class="podium-base podium-base-1">1</div>
          </div>
          <div class="podium-item podium-3">
            <div class="podium-avatar">
              <div class="avatar-circle">{{ getInitials(podium[2].representantenome) }}</div>
            </div>
            <div class="podium-name">{{ getFirstName(podium[2].representantenome) }}</div>
            <div class="podium-pts">{{ podium[2].total_points }} pts</div>
            <div class="podium-base podium-base-3">3</div>
          </div>
        </div>

        <div class="ranking-list">
          <div
            v-for="entry in rankingList"
            :key="entry.representante_id"
            class="ranking-row"
            :class="{ 'ranking-row-me': isCurrentUser(entry.representante_id) }"
          >
            <div class="ranking-pos">{{ entry.position }}º</div>
            <div class="ranking-avatar-sm">
              <div class="avatar-circle-sm">{{ getInitials(entry.representantenome) }}</div>
            </div>
            <div class="ranking-info">
              <span class="ranking-name">{{ entry.representantenome }}</span>
            </div>
            <div class="ranking-points-col">
              <span class="ranking-points">{{ entry.total_points }} pts</span>
              <span class="ranking-week-pts">+{{ entry.week_points || 0 }} esta semana</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="dashData.badges && dashData.badges.length" class="badges-section">
        <h6 class="section-title-sm">
          <feather-icon icon="ShieldIcon" size="16" class="mr-50" />
          Suas Medalhas
        </h6>
        <div class="badges-grid">
          <div v-for="badge in dashData.badges" :key="badge.badge_id || badge.id" class="badge-item" :title="badge.descricao">
            <span class="badge-emoji">{{ badge.icone || '🏅' }}</span>
            <span class="badge-name">{{ badge.nome }}</span>
          </div>
        </div>
      </div>

      <div class="company-totals">
        <div class="total-item">
          <feather-icon icon="ZapIcon" size="16" class="mr-50 text-warning" />
          <span class="total-label">Total Empresa:</span>
          <strong>{{ dashData.totalCompanyPoints }} pts</strong>
        </div>
        <div class="total-item">
          <feather-icon icon="TrendingUpIcon" size="16" class="mr-50 text-success" />
          <span class="total-label">Semana Empresa:</span>
          <strong>{{ dashData.totalWeekPoints }} pts</strong>
        </div>
        <div class="total-item">
          <feather-icon icon="UsersIcon" size="16" class="mr-50 text-info" />
          <span class="total-label">Participantes:</span>
          <strong>{{ dashData.totalParticipants }}</strong>
        </div>
      </div>
    </div>

    <b-modal v-model="showGuide" title="Como Ganhar Pontos" size="lg" ok-only ok-title="Entendi!" scrollable>
      <div class="guide-content">
        <p class="guide-intro">Realize ações no sistema e acumule pontos automaticamente! Veja como pontuar:</p>
        <div class="guide-categories">
          <div v-for="(rules, category) in groupedRules" :key="category" class="guide-category">
            <h6 class="guide-cat-title">{{ category }}</h6>
            <div class="guide-rules">
              <div v-for="rule in rules" :key="rule.event_type" class="guide-rule-item">
                <span class="guide-rule-name">{{ rule.event_label }}</span>
                <b-badge variant="primary" pill class="guide-points">+{{ rule.points }} pts</b-badge>
              </div>
            </div>
          </div>
        </div>
        <div v-if="dashData && dashData.missions && dashData.missions.length" class="guide-missions-section">
          <h6 class="guide-cat-title mt-1">Missões Ativas</h6>
          <div v-for="m in activeMissions" :key="m.mission_id || m.id" class="guide-mission-item">
            <div>
              <strong>{{ m.nome }}</strong>
              <span v-if="m.descricao" class="text-muted d-block" style="font-size: 0.8rem;">{{ m.descricao }}</span>
            </div>
            <b-badge variant="success" pill>+{{ m.bonus_points }} pts bônus</b-badge>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import gamificationClient from '@/api/CRM/gamification.client';
import { BSpinner, BBadge, BButton, BModal } from 'bootstrap-vue';

const EVENT_TYPES_MAP = {
  MEETING_SCHEDULED: 'Atividades',
  MEETING_COMPLETED: 'Atividades',
  FOLLOWUP_ON_TIME: 'Atividades',
  VISIT_COMPLETED: 'Atividades',
  PROPOSAL_SENT: 'Comercial',
  OPPORTUNITY_QUALIFIED: 'Comercial',
  DEAL_WON_NEW: 'Negócios',
  DEAL_WON_RENEWAL: 'Negócios',
  DEAL_WON_EXPANSION: 'Negócios',
  CHURN_AVOIDED: 'Negócios',
  CASE_PUBLISHED: 'Marketing',
  NPS_HIGH: 'Qualidade',
  SALES_GOAL_ACHIEVED: 'Metas',
  SALES_GOAL_EXCEEDED: 'Metas',
  NEW_CLIENT_REGISTERED: 'Comercial',
  ORDER_CREATED: 'Comercial',
  ORDER_APPROVED: 'Comercial',
  POST_INSTAGRAM_DKP: 'Marketing',
  POST_LINKEDIN_DKP: 'Marketing',
};

export default {
  name: 'GamificationDashboard',
  components: { BSpinner, BBadge, BButton, BModal },
  data() {
    return {
      loading: false,
      dashData: null,
      showGuide: false,
    };
  },
  computed: {
    representanteId() {
      try {
        const repData = JSON.parse(localStorage.getItem('representanteData'));
        if (repData && repData.representanteid) return repData.representanteid;
      } catch (e) {}
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        if (userData && userData.representanteid) return userData.representanteid;
      } catch (e) {}
      const user = this.$store ? this.$store.state.user : null;
      if (user && user.representanteId) return user.representanteId;
      return null;
    },
    podium() {
      if (!this.dashData || !this.dashData.leaderboard) return [];
      return this.dashData.leaderboard.slice(0, 3);
    },
    rankingList() {
      if (!this.dashData || !this.dashData.leaderboard) return [];
      return this.dashData.leaderboard.slice(3);
    },
    motivationalMessage() {
      if (!this.dashData || !this.dashData.personalStats) return '';
      const rank = this.dashData.personalStats.rank;
      const pts = this.dashData.personalStats.total_points;
      if (pts === 0) return '🚀 Comece agora! Realize suas primeiras ações e acumule pontos!';
      if (rank === 1) return '🏆 Você é o líder! Continue firme para manter a primeira posição!';
      if (rank <= 3) return '🔥 Você está no Top 3! Mantenha o ritmo e conquiste o primeiro lugar!';
      if (this.dashData.personalStats.week_points > 0) return '📈 Você está subindo no ranking! Continue evoluindo!';
      return '💪 Cada ação conta! Participe mais e suba no ranking!';
    },
    motivationalIcon() {
      if (!this.dashData || !this.dashData.personalStats) return 'ZapIcon';
      const rank = this.dashData.personalStats.rank;
      if (rank === 1) return 'AwardIcon';
      if (rank <= 3) return 'TrendingUpIcon';
      return 'ZapIcon';
    },
    motivationalClass() {
      if (!this.dashData || !this.dashData.personalStats) return 'motiv-default';
      const rank = this.dashData.personalStats.rank;
      if (rank === 1) return 'motiv-leader';
      if (rank <= 3) return 'motiv-top3';
      if (this.dashData.personalStats.week_points > 0) return 'motiv-rising';
      return 'motiv-default';
    },
    groupedRules() {
      if (!this.dashData || !this.dashData.rules) return {};
      const groups = {};
      this.dashData.rules.forEach(r => {
        const cat = EVENT_TYPES_MAP[r.event_type] || 'Outros';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(r);
      });
      return groups;
    },
    activeMissions() {
      if (!this.dashData || !this.dashData.missions) return [];
      return this.dashData.missions.filter(m => !m.completed);
    },
  },
  created() {
    this.loadDashboard();
  },
  methods: {
    async loadDashboard() {
      this.loading = true;
      try {
        const repId = this.representanteId || (this.$route && this.$route.query && this.$route.query.rep);
        if (!repId) {
          this.dashData = null;
          return;
        }
        const result = await gamificationClient.getDashboardData(repId);
        if (result && result.campaign) {
          this.dashData = {
            ...result,
            missions: result.missions || [],
            badges: result.badges || [],
            leaderboard: result.leaderboard || [],
            rules: result.rules || [],
            personalStats: result.personalStats || { total_points: 0, week_points: 0, month_points: 0, rank: 0, total_badges: 0, total_transactions: 0 },
            pointsToNext: result.pointsToNext || 0,
            pendingMissions: result.pendingMissions || 0,
          };
        } else {
          this.dashData = null;
        }
      } catch (e) {
        console.error('Erro ao carregar dashboard gamificação:', e);
        this.dashData = null;
      } finally {
        this.loading = false;
      }
    },
    formatDate(d) {
      if (!d) return '-';
      const dt = new Date(d);
      return dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    formatNumber(n) {
      if (!n) return '0,00';
      return Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
    },
    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').filter(Boolean).map(w => w[0]).slice(0, 2).join('').toUpperCase();
    },
    getFirstName(name) {
      if (!name) return '?';
      return name.split(' ')[0];
    },
    isCurrentUser(repId) {
      return String(repId) === String(this.representanteId || this.$route.query.rep);
    },
  },
};
</script>

<style scoped>
/* 
  B2B Premium Palette (Gamification):
  1: #01094e (Deep Navy)
  2: #0575b5 (Ocean Blue)
  3: #00b7d6 (Vibrant Cyan)
  4: #8fdeea (Pale Sky Blue)
  5: #d6f4fa (Ice Blue)
*/

.gamification-dashboard {
  max-width: 900px;
  margin: 0 auto;
  padding: 1rem;
}

.no-campaign {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}
.no-campaign-inner {
  text-align: center;
  color: #0575b5;
}

/* Base Glassmorphism Card Style */
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(143, 222, 234, 0.4);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s ease;
}
.glass-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
  border-color: rgba(0, 183, 214, 0.4);
}

.campaign-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  margin-bottom: 0.75rem;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(143, 222, 234, 0.4);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
  transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.3s ease;
}
.campaign-header:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
  border-color: rgba(0, 183, 214, 0.4);
}
.campaign-header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.campaign-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #0575b5, #00b7d6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 4px 15px rgba(0, 183, 214, 0.4);
}
.campaign-name {
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  color: #01094e;
  letter-spacing: -0.01em;
}
.campaign-period {
  font-size: 0.85rem;
  color: #0575b5;
  opacity: 0.8;
  font-weight: 500;
}
.campaign-header-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.guide-btn {
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 8px;
  border-color: #00b7d6;
  color: #0575b5;
}
.guide-btn:hover {
  background: rgba(0, 183, 214, 0.1);
  color: #00b7d6;
}
.days-badge {
  font-size: 0.8rem;
  padding: 0.4em 0.8em;
  background-color: #16a34a; /* Keeping success green for urgency */
}

/* Motivational Banner */
.motivational-banner {
  border-radius: 10px;
  padding: 0.85rem 1.25rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
}
.motiv-leader {
  background: linear-gradient(135deg, rgba(5, 117, 181, 0.12), rgba(0, 183, 214, 0.12));
  color: #0575b5;
  border: 1px solid rgba(0, 183, 214, 0.2);
}
.motiv-top3 {
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(251, 191, 36, 0.12));
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.2);
}
.motiv-rising {
  background: linear-gradient(135deg, rgba(22, 163, 74, 0.12), rgba(34, 197, 94, 0.12));
  color: #16a34a;
  border: 1px solid rgba(22, 163, 74, 0.2);
}
.motiv-default {
  background: #f2fafc;
  color: #01094e;
  border: 1px solid rgba(143, 222, 234, 0.4);
}

/* Prizes */
.prizes-section {
  background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(10px); border: 1px solid rgba(143, 222, 234, 0.4); border-radius: 12px; box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
  padding: 1.25rem 1.5rem;
  margin-bottom: 0.75rem;
}
.prizes-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #01094e;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}
.prizes-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.prize-card {
  border-radius: 10px;
  padding: 1rem 1.25rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}
.prize-gold {
  background: linear-gradient(135deg, #d4af37, #f3e5ab, #ffd700);
  color: #2c2502;
}
.prize-silver {
  background: linear-gradient(135deg, #718096, #cbd5e1, #a0aec0);
  color: #1a202c;
}
.prize-place {
  font-size: 0.85rem;
  font-weight: 700;
  opacity: 0.9;
  margin-bottom: 0.35rem;
}
.prize-medal {
  font-size: 1.1rem;
}
.prize-value {
  font-size: 1.5rem;
  font-weight: 800;
}
.prize-desc {
  font-size: 0.8rem;
  font-weight: 500;
  opacity: 0.85;
  margin-top: 0.25rem;
}

/* Alerts */
.alerts-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}
.alert-card {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
}
.alert-rank {
  background: rgba(0, 183, 214, 0.1);
  color: #0575b5;
  border: 1px solid rgba(0, 183, 214, 0.2);
}
.alert-missions {
  background: rgba(22, 163, 74, 0.08);
  color: #16a34a;
  border: 1px solid rgba(22, 163, 74, 0.15);
}
.alert-deadline {
  background: rgba(245, 158, 11, 0.08);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.15);
}

/* Stats */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}
.stat-card {
  background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(10px); border: 1px solid rgba(143, 222, 234, 0.4); border-radius: 12px; box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}
.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(1, 9, 78, 0.08);
  border-color: rgba(0, 183, 214, 0.4);
}
.stat-main {
  display: flex;
  flex-direction: column;
}
.stat-value-lg {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.1;
  color: #01094e;
}
.stat-positive {
  color: #16a34a;
}
.stat-label-sm {
  font-size: 0.8rem;
  font-weight: 600;
  color: #0575b5;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-top: 0.25rem;
}
.stat-icon-box {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}
.stat-points .stat-icon-box { background: rgba(5, 117, 181, 0.1); color: #0575b5; border: 1px solid rgba(0, 183, 214, 0.15); }
.stat-rank .stat-icon-box { background: rgba(245, 158, 11, 0.1); color: #f59e0b; border: 1px solid rgba(245, 158, 11, 0.15); }
.stat-week .stat-icon-box { background: rgba(22, 163, 74, 0.1); color: #16a34a; border: 1px solid rgba(22, 163, 74, 0.15); }
.stat-badges .stat-icon-box { background: rgba(139, 92, 246, 0.1); color: #8b5cf6; border: 1px solid rgba(139, 92, 246, 0.15); }
.stat-points { border-left: 4px solid #0575b5; }
.stat-rank { border-left: 4px solid #f59e0b; }
.stat-week { border-left: 4px solid #16a34a; }
.stat-badges { border-left: 4px solid #8b5cf6; }

.stat-card:hover .stat-icon-box {
  color: #fff;
  border-color: transparent;
}
.stat-card.stat-points:hover .stat-icon-box { background: linear-gradient(135deg, #0575b5, #00b7d6); box-shadow: 0 4px 15px rgba(0, 183, 214, 0.4); }
.stat-card.stat-rank:hover .stat-icon-box { background: linear-gradient(135deg, #f59e0b, #fbbf24); box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4); }
.stat-card.stat-week:hover .stat-icon-box { background: linear-gradient(135deg, #16a34a, #4ade80); box-shadow: 0 4px 15px rgba(22, 163, 74, 0.4); }
.stat-card.stat-badges:hover .stat-icon-box { background: linear-gradient(135deg, #8b5cf6, #c084fc); box-shadow: 0 4px 15px rgba(139, 92, 246, 0.4); }

/* Ranking Section */
.ranking-section {
  background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(10px); border: 1px solid rgba(143, 222, 234, 0.4); border-radius: 12px; box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
  padding: 1.5rem;
  margin-bottom: 1rem;
}
.ranking-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}
.ranking-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #01094e;
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;
}
.ranking-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #0575b5;
  font-weight: 500;
}
.meta-highlight {
  color: #00b7d6;
  font-weight: 700;
}
.meta-sep {
  color: rgba(143, 222, 234, 0.6);
}

.podium {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 0.75rem;
  margin-bottom: 2rem;
  padding: 0 1rem;
}
.podium-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100px;
}
.podium-crown {
  font-size: 1.75rem;
  margin-bottom: 0.35rem;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2));
}
.podium-avatar {
  margin-bottom: 0.5rem;
}
.avatar-circle {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #f2fafc;
  border: 2px solid rgba(143, 222, 234, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 700;
  color: #0575b5;
}
.avatar-gold {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  border-color: #fbbf24;
  color: #fff;
  width: 64px;
  height: 64px;
  font-size: 1.1rem;
  box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
}
.podium-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #01094e;
  text-align: center;
}
.podium-name-1 {
  font-size: 1.05rem;
}
.podium-pts {
  font-size: 0.8rem;
  color: #0575b5;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.podium-pts-1 {
  color: #d97706;
  font-weight: 800;
}
.podium-base {
  width: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 800;
  color: #fff;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 -4px 10px rgba(0,0,0,0.05);
}
.podium-base-1 {
  background: linear-gradient(0deg, #0575b5, #00b7d6);
  height: 64px;
  width: 100px;
}
.podium-base-2 {
  background: linear-gradient(0deg, #64748b, #94a3b8);
  height: 48px;
}
.podium-base-3 {
  background: linear-gradient(0deg, #8fdeea, #bae6fd);
  color: #0575b5;
  height: 36px;
}

.ranking-list {
  border-top: 1px solid rgba(143, 222, 234, 0.3);
  padding-top: 1rem;
}
.ranking-row {
  display: flex;
  align-items: center;
  padding: 0.625rem 1rem;
  border-radius: 10px;
  transition: all 0.2s;
  border: 1px solid transparent;
}
.ranking-row:hover {
  background: rgba(0, 183, 214, 0.05);
  border-color: rgba(143, 222, 234, 0.3);
}
.ranking-row-me {
  background: rgba(0, 183, 214, 0.08);
  border: 1px solid rgba(0, 183, 214, 0.3);
  box-shadow: 0 4px 15px rgba(0, 183, 214, 0.1);
}
.ranking-pos {
  width: 40px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #0575b5;
}
.ranking-avatar-sm {
  margin-right: 0.75rem;
}
.avatar-circle-sm {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(143, 222, 234, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: #0575b5;
}
.ranking-info {
  flex: 1;
}
.ranking-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #01094e;
}
.ranking-points-col {
  text-align: right;
}
.ranking-points {
  font-size: 1rem;
  font-weight: 800;
  color: #01094e;
  display: block;
}
.ranking-week-pts {
  font-size: 0.75rem;
  color: #16a34a;
  font-weight: 600;
}

.badges-section {
  background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(10px); border: 1px solid rgba(143, 222, 234, 0.4); border-radius: 12px; box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
  padding: 1.25rem 1.5rem;
  margin-bottom: 1rem;
}
.section-title-sm {
  font-size: 1rem;
  font-weight: 700;
  color: #01094e;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}
.badges-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
}
.badge-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 183, 214, 0.08);
  border: 1px solid rgba(0, 183, 214, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 30px;
  font-size: 0.85rem;
  transition: all 0.2s;
}
.badge-item:hover {
  background: rgba(0, 183, 214, 0.15);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 183, 214, 0.15);
}
.badge-emoji {
  font-size: 1.2rem;
}
.badge-name {
  font-weight: 600;
  color: #01094e;
}

.company-totals {
  display: flex;
  justify-content: center;
  gap: 2.5rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.85); backdrop-filter: blur(10px); border: 1px solid rgba(143, 222, 234, 0.4); border-radius: 12px; box-shadow: 0 8px 32px rgba(1, 9, 78, 0.04);
}
.total-item {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  color: #0575b5;
  font-weight: 500;
}
.total-label {
  margin-right: 0.5rem;
}
.total-item strong {
  color: #01094e;
  font-size: 1.05rem;
  font-weight: 800;
}

.guide-content { font-size: 0.9rem; color: #01094e; }
.guide-intro { color: #0575b5; margin-bottom: 1.25rem; font-weight: 500;}
.guide-category { margin-bottom: 1rem; }
.guide-cat-title { font-size: 0.95rem; font-weight: 700; color: #00b7d6; margin-bottom: 0.5rem; padding-bottom: 0.25rem; border-bottom: 1px solid rgba(143, 222, 234, 0.3); }
.guide-rules { display: flex; flex-direction: column; gap: 0.35rem; }
.guide-rule-item { display: flex; justify-content: space-between; align-items: center; padding: 0.35rem 0.6rem; border-radius: 6px; }
.guide-rule-item:hover { background: rgba(0, 183, 214, 0.05); }
.guide-rule-name { font-weight: 500; }
.guide-points { font-size: 0.75rem; background-color: #0575b5; }
.guide-mission-item { display: flex; justify-content: space-between; align-items: center; padding: 0.5rem 0.75rem; border-radius: 8px; margin-bottom: 0.35rem; border: 1px solid rgba(143, 222, 234, 0.3); background: #f2fafc; }

@media (max-width: 768px) {
  .campaign-header { flex-direction: column; align-items: flex-start; gap: 1rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .prizes-row { grid-template-columns: 1fr; }
  .podium { gap: 0.5rem; }
  .podium-item { min-width: 80px; }
  .company-totals { flex-direction: column; gap: 0.75rem; align-items: center; }
  .alerts-row { grid-template-columns: 1fr; }
}

@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
}
</style>

<style>
/* ================= Dark Layout Overrides ================= */
body.dark-layout .gamification-dashboard .campaign-header,
body.dark-layout .gamification-dashboard .prizes-section,
body.dark-layout .gamification-dashboard .stat-card,
body.dark-layout .gamification-dashboard .ranking-section,
body.dark-layout .gamification-dashboard .badges-section,
body.dark-layout .gamification-dashboard .company-totals {
  background: rgba(1, 9, 78, 0.6) !important;
  border-color: rgba(143, 222, 234, 0.1) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4) !important;
}

body.dark-layout .gamification-dashboard .campaign-header:hover,
body.dark-layout .gamification-dashboard .stat-card:hover {
  background: rgba(1, 9, 78, 0.8) !important;
  border-color: rgba(0, 183, 214, 0.3) !important;
  box-shadow: 0 12px 40px rgba(0, 183, 214, 0.15) !important;
}

body.dark-layout .gamification-dashboard .campaign-name,
body.dark-layout .gamification-dashboard .stat-value-lg,
body.dark-layout .gamification-dashboard .ranking-name,
body.dark-layout .gamification-dashboard .ranking-points,
body.dark-layout .gamification-dashboard .podium-name,
body.dark-layout .gamification-dashboard .ranking-title,
body.dark-layout .gamification-dashboard .badge-name,
body.dark-layout .gamification-dashboard .section-title-sm,
body.dark-layout .gamification-dashboard .prizes-title,
body.dark-layout .guide-rule-name,
body.dark-layout .gamification-dashboard .total-item strong {
  color: #d6f4fa !important;
}

body.dark-layout .gamification-dashboard .campaign-period,
body.dark-layout .gamification-dashboard .stat-label-sm,
body.dark-layout .gamification-dashboard .ranking-meta,
body.dark-layout .gamification-dashboard .ranking-pos,
body.dark-layout .gamification-dashboard .podium-pts,
body.dark-layout .gamification-dashboard .total-item {
  color: #8fdeea !important;
}

body.dark-layout .gamification-dashboard .ranking-row:hover {
  background: rgba(0, 183, 214, 0.1) !important;
  border-color: rgba(0, 183, 214, 0.2) !important;
}

body.dark-layout .gamification-dashboard .ranking-row-me {
  background: rgba(0, 183, 214, 0.15) !important;
  border-color: rgba(0, 183, 214, 0.4) !important;
  box-shadow: 0 4px 15px rgba(0, 183, 214, 0.2) !important;
}

body.dark-layout .gamification-dashboard .motiv-default {
  background: rgba(1, 9, 78, 0.6) !important;
  color: #8fdeea !important;
  border-color: rgba(143, 222, 234, 0.1) !important;
}

body.dark-layout .gamification-dashboard .ranking-list {
  border-color: rgba(143, 222, 234, 0.15) !important;
}

body.dark-layout .gamification-dashboard .avatar-circle,
body.dark-layout .gamification-dashboard .avatar-circle-sm {
  background: rgba(1, 9, 78, 0.8) !important;
  border-color: rgba(143, 222, 234, 0.3) !important;
  color: #8fdeea !important;
}

body.dark-layout .gamification-dashboard .badge-item {
  background: rgba(0, 183, 214, 0.1) !important;
  border-color: rgba(0, 183, 214, 0.2) !important;
}
body.dark-layout .gamification-dashboard .badge-item:hover {
  background: rgba(0, 183, 214, 0.2) !important;
  box-shadow: 0 4px 10px rgba(0, 183, 214, 0.3) !important;
}
</style>
